import { AdminApiError, type AdminClient, type SicboMultipliersOverride } from '@apx/admin-sdk';
import { sicboMultipliersSchema } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { classifyReturn } from './inheritance.js';

export interface SicboMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

const BPS_SCALE = 10000;

// Ordered bet types EXCLUDING the six singles — those collapse into one
// `singleWinningsPerDieBps` lever below. Mirrors SICBO_BET_TYPES (minus singles)
// in @apx/game-sicbo; kept local to avoid an engine→games dependency.
type SicboReturnKey =
  | 'SICBO_BIG' | 'SICBO_SMALL' | 'SICBO_ODD' | 'SICBO_EVEN'
  | 'SICBO_TRIPLE_ANY'
  | 'SICBO_TRIPLE_1' | 'SICBO_TRIPLE_2' | 'SICBO_TRIPLE_3'
  | 'SICBO_TRIPLE_4' | 'SICBO_TRIPLE_5' | 'SICBO_TRIPLE_6'
  | 'SICBO_TOTAL_4' | 'SICBO_TOTAL_5' | 'SICBO_TOTAL_6' | 'SICBO_TOTAL_7'
  | 'SICBO_TOTAL_8' | 'SICBO_TOTAL_9' | 'SICBO_TOTAL_10' | 'SICBO_TOTAL_11'
  | 'SICBO_TOTAL_12' | 'SICBO_TOTAL_13' | 'SICBO_TOTAL_14' | 'SICBO_TOTAL_15'
  | 'SICBO_TOTAL_16' | 'SICBO_TOTAL_17'
  | 'SICBO_DOUBLE_1' | 'SICBO_DOUBLE_2' | 'SICBO_DOUBLE_3'
  | 'SICBO_DOUBLE_4' | 'SICBO_DOUBLE_5' | 'SICBO_DOUBLE_6';

const RETURN_KEYS: SicboReturnKey[] = [
  'SICBO_BIG', 'SICBO_SMALL', 'SICBO_ODD', 'SICBO_EVEN',
  'SICBO_TRIPLE_ANY',
  'SICBO_TRIPLE_1', 'SICBO_TRIPLE_2', 'SICBO_TRIPLE_3',
  'SICBO_TRIPLE_4', 'SICBO_TRIPLE_5', 'SICBO_TRIPLE_6',
  'SICBO_TOTAL_4', 'SICBO_TOTAL_5', 'SICBO_TOTAL_6', 'SICBO_TOTAL_7',
  'SICBO_TOTAL_8', 'SICBO_TOTAL_9', 'SICBO_TOTAL_10', 'SICBO_TOTAL_11',
  'SICBO_TOTAL_12', 'SICBO_TOTAL_13', 'SICBO_TOTAL_14', 'SICBO_TOTAL_15',
  'SICBO_TOTAL_16', 'SICBO_TOTAL_17',
  'SICBO_DOUBLE_1', 'SICBO_DOUBLE_2', 'SICBO_DOUBLE_3',
  'SICBO_DOUBLE_4', 'SICBO_DOUBLE_5', 'SICBO_DOUBLE_6',
];

// Built-in total-return bps defaults — mirrors DEFAULT_SICBO_RETURN_BPS in
// @apx/game-sicbo. Shown when no override exists yet. Exported so a parity test
// (multipliers-defaults.test.ts) can assert it never drifts from the package
// constant.
export const SICBO_DEFAULT_RETURNS: Record<SicboReturnKey, number> = {
  SICBO_BIG: 20000, SICBO_SMALL: 20000, SICBO_ODD: 20000, SICBO_EVEN: 20000,
  SICBO_TRIPLE_ANY: 310000,
  SICBO_TRIPLE_1: 1810000, SICBO_TRIPLE_2: 1810000, SICBO_TRIPLE_3: 1810000,
  SICBO_TRIPLE_4: 1810000, SICBO_TRIPLE_5: 1810000, SICBO_TRIPLE_6: 1810000,
  SICBO_TOTAL_4: 610000, SICBO_TOTAL_5: 310000, SICBO_TOTAL_6: 180000, SICBO_TOTAL_7: 130000,
  SICBO_TOTAL_8: 90000, SICBO_TOTAL_9: 70000, SICBO_TOTAL_10: 70000, SICBO_TOTAL_11: 70000,
  SICBO_TOTAL_12: 70000, SICBO_TOTAL_13: 90000, SICBO_TOTAL_14: 130000, SICBO_TOTAL_15: 180000,
  SICBO_TOTAL_16: 310000, SICBO_TOTAL_17: 610000,
  SICBO_DOUBLE_1: 110000, SICBO_DOUBLE_2: 110000, SICBO_DOUBLE_3: 110000,
  SICBO_DOUBLE_4: 110000, SICBO_DOUBLE_5: 110000, SICBO_DOUBLE_6: 110000,
};

// Mirrors DEFAULT_SINGLE_WINNINGS_PER_DIE_BPS in @apx/game-sicbo. Exported for
// the parity test.
export const SICBO_DEFAULT_SINGLE_WINNINGS_PER_DIE_BPS = 10000;

const DEFAULT_VALUE: SicboMultipliersOverride = {
  version: 1,
  returns: { ...SICBO_DEFAULT_RETURNS },
  singleWinningsPerDieBps: SICBO_DEFAULT_SINGLE_WINNINGS_PER_DIE_BPS,
};

/** Human hint: total-return bps → "x.xx× return (n:1)". */
function returnHint(bps: number): string {
  const ret = bps / BPS_SCALE;
  const odds = (bps - BPS_SCALE) / BPS_SCALE;
  return `${ret.toFixed(2)}× return (${odds.toFixed(2)}:1)`;
}

function rowHtml(key: SicboReturnKey, isIntegratorScope: boolean): string {
  const tagCell = isIntegratorScope
    ? `<td data-tag="${key}" class="muted" style="min-width:80px">—</td>`
    : `<td class="muted" data-default="${key}">default ${SICBO_DEFAULT_RETURNS[key]} bps</td>`;
  const revertCell = isIntegratorScope
    ? `<td><button data-revert="${key}" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button></td>`
    : `<td></td>`;
  return `<tr data-row="${key}" data-source="inherited">
    <td><strong>${key.replace(/^SICBO_/, '')}</strong></td>
    <td><input data-bet="${key}" type="number" min="0" max="100000000" step="1" style="width:110px" /></td>
    <td class="muted" data-hint="${key}">—</td>
    ${tagCell}
    ${revertCell}
  </tr>`;
}

/**
 * Sicbo payout-multiplier editor. One total-return-bps input per bet type
 * EXCEPT the six single-number bets, which collapse to a single
 * `singleWinningsPerDieBps` lever (winnings are count-scaled per matching die).
 * Values are total-return basis points: 20000 = 2.0× return = 1:1 win. Changes
 * ride the two-admin approval flow. Live RTP (real settled rounds) shown for
 * sanity-checking.
 *
 * In integrator scope: rows show inherited/override tags and per-row revert.
 * readForm() only includes override keys in the returned `returns` map.
 * In provider scope: full table submitted, no inheritance UI.
 */
export async function renderSicboMultipliersPage(
  root: HTMLElement,
  deps: SicboMultipliersPageDeps,
): Promise<void> {
  const isIntegratorScope = !deps.isProviderScope;

  const resetAllBtn = isIntegratorScope
    ? `<button id="sm-reset-all" class="ghost">Reset all to provider default</button>`
    : '';

  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — Sic Bo</h1>
    <p class="page-sub">Total-return per unit stake in basis points: <code>20000 = 2.0× return = 1:1 win</code>. The six single-number bets share one per-die winnings lever. Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Single-number bets</h2>
      <p class="muted" style="margin-top:-4px">Winnings per matching die, in bps. <code>10000 = +1× per die</code>. Total return scales with the number of matching dice.</p>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <label>singleWinningsPerDieBps
          <input id="sm-single" type="number" min="0" max="100000000" step="1" style="width:140px" />
        </label>
        <span class="muted" id="sm-single-hint">—</span>
        ${isIntegratorScope ? `<span id="sm-single-tag" style="font-size:12px;font-weight:600;color:var(--muted)">—</span>` : ''}
        ${isIntegratorScope ? `<button id="sm-single-revert" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button>` : ''}
      </div>
    </div>

    <div class="card">
      <h2>Per-bet-type return (bps)</h2>
      <table>
        <thead><tr><th>Bet type</th><th>Return (bps)</th><th>Multiple</th>${isIntegratorScope ? '<th>Source</th><th></th>' : '<th></th><th></th>'}</tr></thead>
        <tbody id="sm-rows">${RETURN_KEYS.map((k) => rowHtml(k, isIntegratorScope)).join('')}</tbody>
      </table>
      <div class="row" style="margin-top:14px">
        <button id="sm-reset" class="ghost">Reset to defaults</button>
        ${resetAllBtn}
        <button id="sm-save" class="primary">Propose change</button>
        <span id="sm-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="sm-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rowsEl = root.querySelector<HTMLElement>('#sm-rows')!;
  const singleEl = root.querySelector<HTMLInputElement>('#sm-single')!;
  const singleHintEl = root.querySelector<HTMLElement>('#sm-single-hint')!;
  const singleTagEl = root.querySelector<HTMLElement>('#sm-single-tag');
  const singleRevertEl = root.querySelector<HTMLButtonElement>('#sm-single-revert');
  const msgEl = root.querySelector<HTMLElement>('#sm-msg')!;

  // Per-row inherited values for revert (integrator scope only)
  const inheritedValues = new Map<SicboReturnKey, number>();
  let inheritedSingleBps = SICBO_DEFAULT_SINGLE_WINNINGS_PER_DIE_BPS;
  // Track scalar override flag separately (true = admin has edited it)
  let singleIsOverride = false;

  const inputs = new Map<SicboReturnKey, HTMLInputElement>();
  for (const key of RETURN_KEYS) {
    const el = rowsEl.querySelector<HTMLInputElement>(`input[data-bet="${key}"]`);
    if (el) {
      inputs.set(key, el);
      el.addEventListener('input', () => updateHint(key));
    }
  }

  function updateHint(key: SicboReturnKey): void {
    const hintEl = rowsEl.querySelector<HTMLElement>(`[data-hint="${key}"]`);
    if (hintEl) hintEl.textContent = returnHint(Number(inputs.get(key)?.value) || 0);
  }

  singleEl.addEventListener('input', () => {
    const v = Number(singleEl.value) || 0;
    singleHintEl.textContent = `${(v / BPS_SCALE).toFixed(2)}× winnings per matching die`;
  });

  /** Mark a row as override (integrator scope only). */
  function markRowOverride(key: SicboReturnKey): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    if (row) row.dataset.source = 'override';
    if (tagEl) {
      tagEl.textContent = 'override';
      tagEl.style.color = '#f5c542';
      tagEl.style.fontWeight = '600';
    }
    if (revertEl) revertEl.style.display = '';
  }

  /** Mark a row as inherited (integrator scope only). */
  function markRowInherited(key: SicboReturnKey): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    if (row) row.dataset.source = 'inherited';
    if (tagEl) {
      tagEl.textContent = 'inherited';
      tagEl.style.color = 'var(--muted)';
      tagEl.style.fontWeight = '';
    }
    if (revertEl) revertEl.style.display = 'none';
  }

  /** Mark the scalar single-die input as override. */
  function markSingleOverride(): void {
    singleIsOverride = true;
    if (singleTagEl) {
      singleTagEl.textContent = 'override';
      singleTagEl.style.color = '#f5c542';
      singleTagEl.style.fontWeight = '600';
    }
    if (singleRevertEl) singleRevertEl.style.display = '';
  }

  /** Mark the scalar single-die input as inherited. */
  function markSingleInherited(): void {
    singleIsOverride = false;
    if (singleTagEl) {
      singleTagEl.textContent = 'inherited';
      singleTagEl.style.color = 'var(--muted)';
      singleTagEl.style.fontWeight = '';
    }
    if (singleRevertEl) singleRevertEl.style.display = 'none';
  }

  /** Seed rows from a full override value (provider scope or reset). */
  function fillForm(value: SicboMultipliersOverride): void {
    const returns = (value.returns ?? {}) as Record<string, number>;
    for (const [key, input] of inputs) {
      input.value = String(returns[key] ?? SICBO_DEFAULT_RETURNS[key]);
      updateHint(key);
    }
    singleEl.value = String(value.singleWinningsPerDieBps ?? SICBO_DEFAULT_SINGLE_WINNINGS_PER_DIE_BPS);
    singleEl.dispatchEvent(new Event('input'));
  }

  /** Seed rows using inheritance classification (integrator scope). */
  function fillFormInherited(
    ownReturns: Record<string, number> | undefined,
    providerReturns: Record<string, number> | undefined,
    ownSingleBps: number | undefined,
    providerSingleBps: number | undefined,
  ): void {
    for (const [key, input] of inputs) {
      const classified = classifyReturn(key, ownReturns, providerReturns, SICBO_DEFAULT_RETURNS[key]);
      input.value = String(classified.value);
      // Store inherited fallback (provider if set, else baked default)
      const inheritedVal =
        providerReturns && Object.prototype.hasOwnProperty.call(providerReturns, key)
          ? providerReturns[key]!
          : SICBO_DEFAULT_RETURNS[key];
      inheritedValues.set(key, inheritedVal);
      updateHint(key);
      if (classified.source === 'override') {
        markRowOverride(key);
      } else {
        markRowInherited(key);
      }
    }

    // Scalar: singleWinningsPerDieBps
    const effectiveSingle =
      ownSingleBps !== undefined
        ? ownSingleBps
        : providerSingleBps !== undefined
          ? providerSingleBps
          : SICBO_DEFAULT_SINGLE_WINNINGS_PER_DIE_BPS;
    inheritedSingleBps =
      providerSingleBps !== undefined ? providerSingleBps : SICBO_DEFAULT_SINGLE_WINNINGS_PER_DIE_BPS;
    singleEl.value = String(effectiveSingle);
    singleEl.dispatchEvent(new Event('input'));
    if (ownSingleBps !== undefined) {
      markSingleOverride();
    } else {
      markSingleInherited();
    }
  }

  /** Build the propose payload. In integrator scope, only includes override keys. */
  function readForm(): SicboMultipliersOverride {
    if (deps.isProviderScope) {
      // Provider scope: full table
      const returns: Record<string, number> = {};
      for (const [key, input] of inputs) returns[key] = Math.round(Number(input.value) || 0);
      return {
        version: 1,
        returns,
        singleWinningsPerDieBps: Math.round(Number(singleEl.value) || 0),
      } as SicboMultipliersOverride;
    }
    // Integrator scope: partial returns — only keys marked override
    const returns: Record<string, number> = {};
    for (const [key, input] of inputs) {
      const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
      if (row?.dataset.source === 'override') {
        returns[key] = Math.round(Number(input.value) || 0);
      }
    }
    return {
      version: 1,
      returns,
      singleWinningsPerDieBps: singleIsOverride
        ? Math.round(Number(singleEl.value) || 0)
        : inheritedSingleBps,
    } as SicboMultipliersOverride;
  }

  // Wire per-row input → mark override on edit (integrator scope only)
  if (isIntegratorScope) {
    for (const [key, input] of inputs) {
      input.addEventListener('input', () => {
        markRowOverride(key);
        updateHint(key);
      });
    }
    singleEl.addEventListener('input', () => {
      markSingleOverride();
    });

    // Per-row revert buttons
    for (const key of RETURN_KEYS) {
      const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
      if (revertEl) {
        revertEl.addEventListener('click', () => {
          const inherited = inheritedValues.get(key) ?? SICBO_DEFAULT_RETURNS[key];
          const input = inputs.get(key);
          if (input) {
            input.value = String(inherited);
            updateHint(key);
          }
          markRowInherited(key);
        });
      }
    }

    // Single-die revert
    if (singleRevertEl) {
      singleRevertEl.addEventListener('click', () => {
        singleEl.value = String(inheritedSingleBps);
        singleEl.dispatchEvent(new Event('input'));
        markSingleInherited();
      });
    }

    // Reset all to provider default
    const resetAllEl = root.querySelector<HTMLButtonElement>('#sm-reset-all');
    if (resetAllEl) {
      resetAllEl.addEventListener('click', () => {
        for (const [key, input] of inputs) {
          const inherited = inheritedValues.get(key) ?? SICBO_DEFAULT_RETURNS[key];
          input.value = String(inherited);
          updateHint(key);
          markRowInherited(key);
        }
        singleEl.value = String(inheritedSingleBps);
        singleEl.dispatchEvent(new Event('input'));
        markSingleInherited();
        msgEl.textContent = 'All rows reset to provider/baked defaults (not saved).';
        msgEl.className = 'muted';
      });
    }
  }

  // Load current data
  if (isIntegratorScope) {
    const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
    const ownMult = inh.ownOverride.multipliers as Partial<SicboMultipliersOverride> | undefined;
    const provMult = inh.providerDefault.multipliers as Partial<SicboMultipliersOverride> | undefined;
    fillFormInherited(
      ownMult?.returns as Record<string, number> | undefined,
      provMult?.returns as Record<string, number> | undefined,
      ownMult?.singleWinningsPerDieBps,
      provMult?.singleWinningsPerDieBps,
    );
  } else {
    const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
    const stored = current as Partial<SicboMultipliersOverride> | null;
    fillForm(stored && stored.returns ? (stored as SicboMultipliersOverride) : DEFAULT_VALUE);
  }

  root.querySelector<HTMLButtonElement>('#sm-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    if (isIntegratorScope) {
      // After reset to module defaults, mark all as override (admin explicitly set them)
      for (const key of RETURN_KEYS) markRowOverride(key);
      markSingleOverride();
    }
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#sm-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = sicboMultipliersSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data as SicboMultipliersOverride,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#sm-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — payout multipliers now live.';
          msgEl.className = 'muted';
          await refreshPending();
          if (isIntegratorScope) {
            const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
            const ownMult = inh.ownOverride.multipliers as Partial<SicboMultipliersOverride> | undefined;
            const provMult = inh.providerDefault.multipliers as Partial<SicboMultipliersOverride> | undefined;
            fillFormInherited(
              ownMult?.returns as Record<string, number> | undefined,
              provMult?.returns as Record<string, number> | undefined,
              ownMult?.singleWinningsPerDieBps,
              provMult?.singleWinningsPerDieBps,
            );
          } else {
            const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
            const value = fresh.value as Partial<SicboMultipliersOverride> | null;
            fillForm(value && value.returns ? (value as SicboMultipliersOverride) : DEFAULT_VALUE);
          }
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
  }
  await refreshPending();
}
